import { fileURLToPath } from 'node:url';
import type { WixIntegration } from '@wix/astro-core';
import { normalizePath } from '@wix/fs-utils';
import { outdent } from 'outdent';
import { patchAstroInlineScripts } from './plugins/patchAstroInlineScripts.js';
import { setupContentCollectionContext } from './plugins/setupContentCollectionContext.js';

const setupContextUrl = new URL(
  '../build-browser-runtime/setup.js',
  import.meta.url
);

const createIntegration = ({
  enableAuthRoutes,
}: {
  enableAuthRoutes: boolean;
}): WixIntegration => {
  return {
    name: '@wix/astro/auth',
    hooks: {
      'astro:config:setup'({
        addMiddleware,
        config,
        injectRoute,
        injectScript,
        updateConfig,
      }) {
        const srcDir = fileURLToPath(config.srcDir);

        updateConfig({
          vite: {
            plugins: [
              setupContentCollectionContext(srcDir),
              patchAstroInlineScripts(),
            ],
          },
        });

        const setupContextCode = outdent`
          import { WIX_CLIENT_ID } from 'astro:env/client';
          import { setup } from '${normalizePath(setupContextUrl)}';

          setup({ clientId: WIX_CLIENT_ID });
        `;

        // support client-side context calls
        injectScript('before-hydration', setupContextCode);
        injectScript('page', setupContextCode);

        // support server side context calls
        addMiddleware({
          entrypoint: new URL(
            '../build-runtime/middleware/auth.js',
            import.meta.url
          ),
          order: 'pre',
        });

        // support built-in auth routes
        if (enableAuthRoutes) {
          injectRoute({
            entrypoint: new URL(
              '../build-runtime/routes/login.js',
              import.meta.url
            ),
            pattern: '/api/auth/login',
            prerender: false,
          });

          injectRoute({
            entrypoint: new URL(
              '../build-runtime/routes/logout.js',
              import.meta.url
            ),
            pattern: '/api/auth/logout',
            prerender: false,
          });

          injectRoute({
            entrypoint: new URL(
              '../build-runtime/routes/callback.js',
              import.meta.url
            ),
            pattern: '/api/auth/callback',
            prerender: false,
          });

          injectRoute({
            entrypoint: new URL(
              '../build-runtime/routes/logout-callback.js',
              import.meta.url
            ),
            pattern: '/api/auth/logout-callback',
            prerender: false,
          });
        }
      },
    },
  };
};

export default createIntegration;
